<template>
  <div data-testid="productCardVertical">
    <ProductCardVerticalInner
      :image="product.image"
      :title="product.title"
      :qty="product.qty"
      :sku="productSku"
      :full-product-price="totalPriceBeforeDiscountsForItem"
      :discounted-price="finalPriceForItem"
      :link="productLink"
      :stock="product.stock"
      :disabled="disabled"
      :read-only="readOnly"
      :unit-price="product.unitPrice"
      :should-display-discount="shouldDisplayDiscount"
      :triggered-in="triggeredIn"
      :energy-rating="energyRating"
      @input-quantity="$emit('inputQuantity', $event)"
      @click-remove="$emit('clickRemove')"
    />
    <ExtraGuaranteeProductCard
      v-if="product.extraGuarantee"
      :title="product.extraGuarantee.title"
      :extra-guarantee-price="getTotalPriceForItem(product.extraGuarantee)"
      :read-only="readOnly"
      @click="handleEditGuarantee"
    />
  </div>
</template>

<script lang="ts">

import type { LineItemWithGuaranteeInside } from '~~/types/product/LineItemExtended';

import { getItemPrice, getTotalPriceForItem, getTotalGrossPriceBeforeDiscount } from '~~/helpers/cart/getTotalPrices';
import type { EnergyRatingDetails } from '~~/types/components/EnergyRating/EnergyRatingDetails';
import { getEnergyRating } from '~~/helpers/product/energyRating';

export default defineComponent({
  name: 'ProductCardVertical',

  props: {
    product: {
      type: Object as PropType<LineItemWithGuaranteeInside>,
      required: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    readOnly: {
      type: Boolean,
      default: false,
    },
    triggeredIn: {
      type: String,
      default: '',
    },
  },
  emits: ['clickRemove', 'inputQuantity'],
  setup(props) {

    const { product } = toRefs(props);
    const { languageAndCountry } = useLocalization();
    const finalPriceForItem = computed(() => (
      product.value.unitPrice !== null ? product.value.unitPrice * product.value.qty : null
    ));

    const totalPriceBeforeDiscountsForItem = computed(() =>
      getTotalPriceForItem(product.value),
    );
    const { isExternalTaxMode, isExtraGuaranteeOnlyCart } = useCartExtended();

    /**
     * The price before discounts is a net amount in external tax mode, so the difference against
     * the final price is the tax the cart charges rather than a discount - with the B2B fallback
     * rate it even reads as a price cut while the customer is paying more. Nothing comparable is
     * left to strike through, so the discount stays hidden for as long as that mode is active.
     */
    const shouldDisplayDiscount = computed(() =>
      !isExternalTaxMode.value && totalPriceBeforeDiscountsForItem.value !== finalPriceForItem.value);
    const {
      setExtraGuaranteeInfo,
    } = useExtraGuarantee();
    const { handleExtraGuaranteeLogic } = useCartSidebars();
    const { toggleCartSidebar } = useCartSidebarStore();
    const { setIsGuaranteeActionBeingClicked } = useLineItems();

    const productSku = computed(() => product.value.variant?.sku);

    const productLink = computed(() => {

      if (isExtraGuaranteeOnlyCart.value) {

        return;

      }

      return `/${product.value.productSlug}`;

    });

    const energyRating = computed<Maybe<EnergyRatingDetails>>(() => {

      if (!product.value.variant) {

        return null;

      }

      return getEnergyRating({ product: product.value.variant, languageAndCountry: languageAndCountry.value });

    });

    const handleEditGuarantee = () => {

      const parentSku = productSku.value;
      const parentItem = product.value;
      setIsGuaranteeActionBeingClicked(true);
      setExtraGuaranteeInfo({
        fullProductPrice: getTotalGrossPriceBeforeDiscount(parentItem),
        name: parentItem.name,
        image: parentItem.variant?.images?.[0]?.url,
        sku: parentSku,
      });
      toggleCartSidebar();
      handleExtraGuaranteeLogic(productSku.value);

    };

    return {
      getItemPrice,
      productSku,
      productLink,
      finalPriceForItem,
      totalPriceBeforeDiscountsForItem ,
      shouldDisplayDiscount,
      energyRating,
      handleEditGuarantee,
      getTotalPriceForItem,
    };

  },
});

</script>
